import {z} from 'zod';
import {placementSchema} from '../placementsManagementTool.schema';
import {
  CampaignEnvironment,
  CampaignPlacementEnvironmentValue,
  Placement_UI,
  CustomParams,
} from 'common/src/modules/campaignPlacements/placements.types';
import formatAdSizeFromString from 'common/src/modules/adSize/utils/formatAdSizeFromString';
import {AdSize} from 'common/src/modules/adSize/adSize.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import isCustomParamOfDateType from 'common/src/modules/campaignPlacements/utils/isCustomParamOfDateType';
import isCustomParamOfBooleanType from 'common/src/modules/campaignPlacements/utils/isCustomParamOfBooleanType';
import {DateTime} from 'luxon';

export default function convertPlacementSchemaToPlacementUI(
  placement: z.infer<typeof placementSchema>,
  campaignEnums?: Record<string, CLEnumsMapper>,
): Pick<
  Placement_UI,
  | 'id'
  | 'name'
  | 'placementId'
  | 'dspId'
  | 'environment'
  | 'customTextParams'
  | 'customDateTimeParams'
  | 'customBooleanParams'
> {
  const customTextParams: CustomParams<string>[] = [];
  const customDateTimeParams: CustomParams<DateTime>[] = [];
  const customBooleanParams: CustomParams<boolean>[] = [];

  // Process custom parameters
  if (placement.customParameters && campaignEnums) {
    const customParamsEnum = campaignEnums[CLEnumKeys.CampaignPage.PlacementCustomParams];

    if (customParamsEnum) {
      for (const customParam of placement.customParameters) {
        const paramOption = customParamsEnum.byName[customParam.name];

        if (!paramOption) {
          continue;
        }

        const paramType = paramOption.value;

        if (isCustomParamOfDateType(customParam.name)) {
          if (DateTime.fromISO(customParam.value as string).isValid) {
            customDateTimeParams.push({
              type: paramType,
              value: DateTime.fromISO(customParam.value as string),
            });
          }
        } else if (isCustomParamOfBooleanType(customParam.name)) {
          if (typeof customParam.value === 'boolean') {
            customBooleanParams.push({
              type: paramType,
              value: customParam.value as boolean,
            });
          }
        } else {
          if (typeof customParam.value === 'string') {
            customTextParams.push({
              type: paramType,
              value: customParam.value as string as string,
            });
          }
        }
      }
    }
  }

  return {
    id: placement.id ?? 0,
    name: placement.name,
    placementId: placement.plcId,
    dspId: placement.dspId,
    ...(!!placement.adSize && {adSize: formatAdSizeFromString(placement.adSize) as AdSize}),
    environment: getMatchingEnumValue(
      CampaignEnvironment,
      CampaignPlacementEnvironmentValue,
      placement.environment,
    ) as number,
    customTextParams,
    customDateTimeParams,
    customBooleanParams,
  };
}
